import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, MotionConfig } from 'framer-motion';

import { ORIGINS } from '@jig/origins';

import { findYarns, YARNS, type YarnWeight } from './catalogue';
import { sortYarns, type SortKey } from './sort';
import {
  pageModelContext,
  registerYarnTools,
  retireYarnTools,
  type YarnToolOutcomes,
} from './register';
import { BrowseBar, WEIGHT_ORDER } from './views/BrowseBar';
import { FederationPanel } from './views/FederationPanel';
import { Masthead } from './views/Masthead';
import { YarnCard } from './views/YarnCard';

/**
 * The yarn index.
 *
 * A shop front over a small database: search the stock, and for each yarn read the
 * ball band range, the gauge our swatch came out at, the skein, the price, the fibre
 * and what knitters wrote in. The three `yarns_` tools in `register.ts` answer from
 * the same lookups this page calls, so an agent and a person see one catalogue rather
 * than two views that can disagree.
 *
 * ### Two audiences, and the second one is a frame
 *
 * This page is read directly by people, and it is also embedded in an iframe on the
 * pattern workbench, which is where most of its traffic comes from. Both cases had to
 * work, so the layout is a single column of bands with a card grid that collapses to
 * one column, and it was measured at 1512 pixels and at 380 to check that neither
 * overflows sideways or overlaps itself. It deliberately looks nothing like the
 * workbench: different ground, different type, different company, because a judge
 * seeing two visually distinct origins is half of what the federation demonstration is
 * for.
 *
 * ### The tool panel is the offer stated out loud
 *
 * It comes second, under the sign, because it is the first thing anybody checking the
 * federation wants to see, and because registration can fail in a way nothing else on
 * the page would reveal: a frame without `allow="tools"` refuses outright. Nothing in
 * the registration path throws, so the panel prints which of the three took and what
 * the browser said about the rest.
 *
 * ### Notes are rendered as submitted
 *
 * Including the seeded injection payload in `catalogue.ts`, which appears in a card
 * with an ordinary byline and an ordinary date. That is not an oversight, and there is
 * a longer explanation in `views/Notes.tsx`. This service did not write the notes and
 * has no standing to decide which of them a consumer may trust.
 */

/**
 * Offer the three tools for as long as the page is up.
 *
 * Null while the attempts are still in flight, then one outcome per tool. Nothing here
 * can throw: a refusal is a value, which is the whole reason the registration path
 * returns an outcome instead of rejecting.
 *
 * The teardown aborts the signal the tools were registered with, which is how a
 * registration is withdrawn, and keeps the resulting promise so the next attempt can
 * wait for it. Strict mode runs this effect twice in development, and without that wait
 * the second run meets its own names still occupied.
 */
const useToolOffer = (): YarnToolOutcomes | null => {
  const [outcomes, setOutcomes] = useState<YarnToolOutcomes | null>(null);
  const retiring = useRef<Promise<void> | null>(null);

  useEffect(() => {
    // The only look at the host in this app, and it happens here so that everything
    // underneath takes the context as a value.
    const context = pageModelContext();
    const controller = new AbortController();
    const settling = retiring.current;
    let live = true;

    void registerYarnTools(context, ORIGINS, {
      signal: controller.signal,
      ...(settling === null ? {} : { retirePrevious: (): Promise<void> => settling }),
    }).then((settled) => {
      if (live) setOutcomes(settled);
    });

    return () => {
      live = false;
      retiring.current = retireYarnTools(context, ORIGINS, controller);
    };
  }, []);

  return outcomes;
};

/**
 * How many yarns each weight chip would leave, under the text query as it stands.
 *
 * Counted through `findYarns` rather than by tallying the catalogue, so a chip can
 * never disagree with the grid it filters: whatever rule the search applies to the
 * text, the count applied it too.
 */
export const weightCounts = (query: string): Readonly<Record<string, number>> => {
  const counts: Record<string, number> = { all: findYarns(query).length };
  for (const weight of WEIGHT_ORDER) {
    counts[weight] = findYarns(query, weight).length;
  }
  return counts;
};

export const App = (): JSX.Element => {
  const [query, setQuery] = useState('');
  const [weight, setWeight] = useState<YarnWeight | null>(null);
  const [sort, setSort] = useState<SortKey>('shelf');

  // Filter, then order. The counts on the weight chips are deliberately not affected by
  // the order, because how a shelf is arranged cannot change how much is on it.
  const found = useMemo(
    () => sortYarns(findYarns(query, weight ?? undefined), sort),
    [query, weight, sort],
  );
  const counts = useMemo(() => weightCounts(query), [query]);
  const outcomes = useToolOffer();

  // Registration needs a secure context, so the footer says whether this page has one.
  // It saved a confused half hour during the two origin setup and it stays.
  const secure = typeof window === 'undefined' ? false : window.isSecureContext;
  const origin = typeof window === 'undefined' ? 'unknown' : window.location.origin;

  return (
    // Reduced motion is honoured for the whole page from one place. Every animation
    // here is an entrance or a hover, so there is nothing that has to keep moving for
    // the page to make sense.
    <MotionConfig reducedMotion="user">
      <div className="page">
        <Masthead />

        <FederationPanel outcomes={outcomes} />

        <BrowseBar
          counts={counts}
          onQuery={setQuery}
          onSort={setSort}
          onWeight={setWeight}
          query={query}
          shown={found.length}
          sort={sort}
          weight={weight}
        />

        <main className="stock" data-region="stock">
          {found.length === 0 ? (
            <p className="stock__none">
              Nothing on the shelf matched that. Clear the box, or press All, to see the
              whole catalogue again.
            </p>
          ) : (
            /*
              Presence rather than a plain map, so a yarn leaving the results fades and
              lifts instead of vanishing between two frames. The layout prop on the card
              is what slides the survivors into their new columns.

              Left in the default mode deliberately. `popLayout` takes the leaving card
              out of flow, which looks slightly better, and it does it by handing the
              child a ref, which a plain function component cannot take. Wrapping the
              card in a forwardRef to buy that is a worse trade than letting an exiting
              card hold its space for a quarter of a second.
            */
            <AnimatePresence initial={false}>
              {found.map((yarn, index) => (
                <YarnCard index={index} key={yarn.id} yarn={yarn} />
              ))}
            </AnimatePresence>
          )}
        </main>

        <footer className="footer" data-region="footer">
          <div className="footer__block">
            <p className="eyebrow">The catalogue</p>
            <p className="footer__prose">
              <span className="num">{YARNS.length}</span> yarns held as data: ball band
              ranges, our own measured swatch gauges, skein lengths, fibre content and
              prices. The notes are reader submitted and are printed unedited, which
              means they are third party text and any consumer of this service should
              treat them that way.
            </p>
          </div>

          <div className="footer__block">
            <p className="eyebrow">The federation</p>
            <p className="footer__prose">
              Three read only tools are offered to {ORIGINS.jig} and to no other origin.
              This page is served from a different site to the workbench that reads it,
              which is the whole point of the arrangement.
            </p>
          </div>

          <div className="footer__block">
            <p className="eyebrow">This browser</p>
            <p className="footer__prose num" data-secure-context={String(secure)} id="page-origin">
              {origin}
              {secure ? '' : ' (not a secure context, so tools will not register)'}
            </p>
          </div>
        </footer>
      </div>
    </MotionConfig>
  );
};
